import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CircleAlert, Eye, EyeOff, LogIn, Sparkles, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage, authService } from '../services/api';
import { Brand, Button, Field, InfinityMark } from '../components/ui';
import './Auth.css';

function Login() {
  const { user, login, loginDemo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);

  useEffect(() => {
    authService.demoStatus().then(d => setDemoEnabled(!!d?.enabled)).catch(() => {});
  }, []);

  // Volta para a página que a pessoa tentou abrir antes do login
  const from = location.state?.from || '/';

  if (user) {
    return <Navigate to={user.mustChangePassword ? '/trocar-senha' : from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const me = await login(username.trim(), password);
      navigate(me.mustChangePassword ? '/trocar-senha' : from, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível entrar. Verifique sua conexão.'));
      setSubmitting(false);
    }
  };

  const handleDemo = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await loginDemo();
      navigate('/', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível abrir a demonstração.'));
      setSubmitting(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__strip" aria-hidden="true" />
      <InfinityMark className="auth__watermark" strokeWidth={4} />

      <div className="auth__box">
        <div className="auth__brand">
          <Brand size="md" onDark />
          <span className="auth__slogan">Estudo sem limite.</span>
        </div>

        <form className="auth__card" onSubmit={handleSubmit}>
          <h1>Entrar</h1>
          <p className="auth__lead">Use o login e a senha que você recebeu.</p>

          {error && (
            <div className="auth__error" role="alert">
              <CircleAlert size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <Field label="Login">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              required
            />
          </Field>

          <Field label="Senha">
            <div className="auth__password">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="ss-iconbtn auth__eye"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </Field>

          <Button type="submit" variant="accent" size="lg" block icon={LogIn} disabled={submitting || !username || !password}>
            {submitting ? 'Entrando...' : 'Entrar'}
          </Button>

          {demoEnabled && (
            <>
              <div className="auth__or" aria-hidden="true"><span>ou</span></div>
              <Button type="button" variant="outline" size="lg" block icon={Sparkles} disabled={submitting} onClick={handleDemo}>
                Ver demonstração
              </Button>
              <p className="auth__demo-note">Uma conta de exemplo com progresso fictício. Não precisa de cadastro.</p>
            </>
          )}

          <div className="auth__request">
            <span className="auth__request-icon"><UserPlus size={18} aria-hidden="true" /></span>
            <span className="auth__request-text">
              <strong>Ainda não tem acesso?</strong>
              <span>Envie uma solicitação para o admin.</span>
            </span>
            <button type="button" onClick={() => navigate('/solicitar-acesso')}>Solicitar acesso</button>
          </div>
        </form>

        <p className="auth__foot">Esqueceu a senha? Peça ao administrador para redefini-la.</p>
        <p className="auth__foot auth__foot--links">
          <button type="button" onClick={() => navigate('/')}>Conheça o StudySpace</button>
          <span aria-hidden="true">·</span>
          <button type="button" onClick={() => navigate('/apresentacao')}>Ver a apresentação</button>
        </p>
      </div>
    </div>
  );
}

export default Login;
